import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Text,
  Group,
  Avatar,
  Button,
  ActionIcon,
  Image,
  Badge,
  LoadingOverlay,
  Loader,
  useMantineTheme,
  Modal,
  Textarea,
} from "@mantine/core";
import { IconHome, IconSend2 } from "@tabler/icons-react";
import botSvg from "../../../assets/Call.svg";

import { InfoPanel } from "../infopanel/infopanel";
import useColors from "../../../theme/useColors";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { formatDate, formatDateWithZoneAbbreviation } from "../../../utils";
import {
  DATE_FORMAT,
} from "../../../constants/app-constant";
import { POLLING_INTERVAL_MS } from "../../../constants/polling-constants";
import { MessageData } from "../../Tables/MessageTable";
import { RouteConstants } from "../../../constants/route-constant";
import { getMessagesListRoute } from "../../../constants/module-nav";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setMessageCount } from "../../../store/messageReducer";
import { parseUnreadConversationCount } from "../../../hooks/queries";
import ResetModal from "../QueryDetails/ResetModal";
import { FhirPractitioner } from "../../../utils/fhir-init-utils";
import FHIR from "fhirclient";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { HtmlViewer } from "../HtmlViewer";
import {
  fetchConversationListQuery,
  fetchConversationMessagesQuery,
  fetchHtmlUrlQuery,
  useConversationList,
  usePostNewMessage,
  useQueryDetailsData,
  useStatusList,
  useUpdateConversationStatus,
  useUpdateMessageReadStatus,
} from "../../../hooks/queries";

export interface Message {
  MESSAGE_ID: string;
  CONVERSATION_ID: string;
  SENDER_ID: string;
  SENDER_TYPE: string;
  MESSAGE_TEXT: string;
  SENT_DATE: string;
  IS_READ: "Y" | "N";
  IS_RECEIVED: string | null;
  LAST_UPDATED: string | null;
  SOURCE: string | null;
  UPDATED_BY: string | null;
}

const MessageBox: React.FC = () => {
  const colors = useColors();
  const dispatch = useAppDispatch();
  const { conversation_id } = useParams<{ conversation_id: string }>();
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();

  const theme = useMantineTheme();
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);

  const [conversationData, setConversationData] = useState<MessageData>(
    state?.conversationData || {}
  );
  const previousRoute = state?.from;
  const provider = useAppSelector((state) => state?.provider?.provider);
  const providerNPI = useAppSelector((state) => state?.provider?.providerNPI);

  const [opened, { open, close }] = useDisclosure(false);

  const [messageList, setMessageList] = React.useState<Message[]>([]);
  const [message, setMessage] = React.useState<string>("");
  const [queryDetails, setQueryDetails] = useState<any>(null);
  const [messagesInitialLoading, setMessagesInitialLoading] =
    useState<boolean>(false);
  const [chatStatusUpdating, setChatStatusUpdating] = useState<boolean>(false);
  const [prevMessageLoading, setPrevMessageLoading] = useState<boolean>(false);
  const [newMessageLoading, setNewMessageLoading] = useState<boolean>(false);
  const [htmlData, setHtmlData] = useState<any>("");
  const [rowID, setRowID] = useState<any>();
  const [htmlViewerLoading, setHtmlViewerLoading] = useState<boolean>(false);

  const [sendingMessageId, setSendingMessageId] = useState<string | null>(null); // Track which message is being sent
  const [failedMessageIds, setFailedMessageIds] = useState<Set<string>>(
    new Set()
  ); // Track failed messages
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"end" | "reopen" | null>(null);
  const [rowStatus, setRowStatus] = useState<any>();

  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [currentPractitionerName, setCurrentPractitionerName] =
    useState<string>("");

  const postNewMessageMutation = usePostNewMessage();
  const { mutateAsync: updateMessageReadStatus } = useUpdateMessageReadStatus();
  const updateConversationStatusMutation = useUpdateConversationStatus();

  const { data: statusListResponse } = useStatusList(Boolean(providerNPI));

  const queryDetailsParams = useMemo(
    () => ({
      query_id: conversationData?.QUERY_ID,
      patient_id: conversationData?.PATIENT_ID,
    }),
    [conversationData?.QUERY_ID, conversationData?.PATIENT_ID]
  );

  const {
    data: queryDetailsResponse,
    isLoading: queryDetailsLoading,
  } = useQueryDetailsData(queryDetailsParams, Boolean(conversationData?.QUERY_ID));

  const conversationListParams = useMemo(
    () => ({
      provider_npi: providerNPI,
      query_id: conversationData?.QUERY_ID,
    }),
    [providerNPI, conversationData?.QUERY_ID]
  );

  const { data: conversationListResponse, refetch: refetchConversationList } =
    useConversationList(conversationListParams, {
      enabled: Boolean(providerNPI && conversationData?.QUERY_ID),
      polling: conversationData?.STATUS === "OPEN",
    });

  useEffect(() => {
    if (queryDetailsResponse?.status === 200) {
      setQueryDetails(queryDetailsResponse.data?.data || {});
    }
  }, [queryDetailsResponse]);

  useEffect(() => {
    if (conversationListResponse?.status === 200) {
      const nextConversation = conversationListResponse.data?.data?.[0];
      if (nextConversation) {
        setConversationData(nextConversation);
      }
    }
  }, [conversationListResponse]);

  const fetchHtmlFromArcPlus = async (id: string): Promise<any | null> => {
    try {
      const response = await fetchHtmlUrlQuery(id);
      return response?.data || null;
    } catch (error) {
      console.error("Failed to fetch HTML URL:", error);
      return null;
    }
  };

  const fetchConversationMessageList = useCallback(
    async (pageNumber = 0) => {
      try {
        const response = await fetchConversationMessagesQuery({
          conversation_id,
          page: pageNumber,
          limit: 10,
        });

        if (response?.status === 200) {
          const newMessages: Message[] = response.data?.data.reverse() || [];

          const unreadMessageIds = newMessages
            .filter((msg) => msg?.SENDER_TYPE !== "PROVIDER")
            .filter((msg) => msg.IS_READ === "N")
            .map((msg) => msg.MESSAGE_ID);

          if (unreadMessageIds.length > 0) {
            await updateMessageReadStatus({
              message_ids: unreadMessageIds,
            });

            const updatedConversationList = await fetchConversationListQuery({
              provider_npi: providerNPI,
            });

            dispatch(
              setMessageCount(
                parseUnreadConversationCount(updatedConversationList?.data)
              )
            );
          }

          setMessageList((prev) => {
            const allMessages = [...prev, ...newMessages];

            const uniqueMessagesMap = new Map<string, Message>();
            allMessages.forEach((msg) => {
              uniqueMessagesMap.set(msg.MESSAGE_ID, msg);
            });

            const sortedMessages = Array.from(uniqueMessagesMap.values()).sort(
              (a, b) => {
                const timeA = a.SENT_DATE
                  ? new Date(a.SENT_DATE).getTime()
                  : parseInt(a.MESSAGE_ID);
                const timeB = b.SENT_DATE
                  ? new Date(b.SENT_DATE).getTime()
                  : parseInt(b.MESSAGE_ID);
                return timeA - timeB;
              }
            );

            return sortedMessages;
          });

          if (newMessages.length === 0) setHasMore(false);
        }
      } catch (error) {
        console.error(
          `Failed to fetch message list for conversation ID ${conversation_id} :`,
          error
        );
      } finally {
        setNewMessageLoading(false);
      }
    },
    [
      conversation_id,
      dispatch,
      providerNPI,
      updateMessageReadStatus,
    ]
  );

  const fetchConversationMessageListRef = useRef(fetchConversationMessageList);
  fetchConversationMessageListRef.current = fetchConversationMessageList;
  const getHtmlData = async (id: any, statusId?: string | number) => {
    setHtmlViewerLoading(true);
    try {
      const responseHtml = await fetchHtmlFromArcPlus(id);
      setHtmlData(responseHtml?.data);

      let statusLabel = "Completed";
      if (statusId) {
        const statusOptions = statusListResponse?.data?.data;
        if (statusOptions) {
          const statusOption = statusOptions.find(
            (option: any) => option.QUERY_STATUS_ID === String(statusId)
          );
          if (statusOption) {
            statusLabel = statusOption.STATUS || statusLabel;
          }
        }
      }

      setRowStatus(statusLabel);
      setRowID(id);
      open();
    } catch (error) {
      console.error("Failed to fetch HTML data:", error);
    } finally {
      setHtmlViewerLoading(false);
    }
  };

  const handleModalClose = () => {
    close();
    // Reset HTML viewer data when modal is closed
    setHtmlData("");
    setRowID(null);
    setRowStatus(null);
  };

  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const container = document.querySelector(".message-scroll-container");

    if (!sentinel || !container || !hasMore) return;

    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (entry.isIntersecting && !messagesInitialLoading && !prevMessageLoading) {
          const prevScrollHeight = container.scrollHeight;

          setPrevMessageLoading(true);
          await fetchConversationMessageList(page + 1);
          setPage((prev) => prev + 1);
          setPrevMessageLoading(false);

          setTimeout(() => {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - prevScrollHeight;
          }, 0);
        }
      },
      {
        root: container,
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [page, messagesInitialLoading, prevMessageLoading, hasMore, fetchConversationMessageList]);

  const handlePostNewMessage = async (
    newMessage: Message,
    providerId: string
  ) => {
    setSendingMessageId(newMessage.MESSAGE_ID); // Track which message is being sent

    try {
      const response = await postNewMessageMutation.mutateAsync({
        conversation_id: newMessage.CONVERSATION_ID,
        provider_npi: providerNPI,
        message: newMessage.MESSAGE_TEXT,
      });

      const serverMessageId = response?.data?.data?.message_id;

      if (response.status === 200 && serverMessageId) {
        setMessageList((prev) =>
          prev.map((msg) =>
            msg.MESSAGE_ID === newMessage.MESSAGE_ID
              ? {
                  ...msg,
                  MESSAGE_ID: serverMessageId,
                  SENT_DATE: response.data?.data?.sent_date || msg.SENT_DATE,
                }
              : msg
          )
        );

        void fetchConversationMessageListRef.current(0);
      }
    } catch (error) {
      console.error("Failed to post new message:", error);
      // Mark message as failed instead of removing it
      setFailedMessageIds(
        (prev) => new Set(Array.from(prev).concat(newMessage.MESSAGE_ID))
      );
    } finally {
      setSendingMessageId(null); // Clear sending state
      handleScrollNewMessage();
    }
  };

  useEffect(() => {
    handleScrollNewMessage();
    setMessagesInitialLoading(true);
    void fetchConversationMessageListRef.current().finally(() => {
      setMessagesInitialLoading(false);
    });

    return () => {
      setMessageList([]);
      setHasMore(true);
      setPage(0);
      topSentinelRef.current = null;
      containerRef.current = null;
    };
  }, [conversation_id]);

  useEffect(() => {
    if (conversationData?.STATUS !== "OPEN") return;

    const intervalId = setInterval(() => {
      setNewMessageLoading(true);
      void fetchConversationMessageListRef.current(0);
      void refetchConversationList();
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [conversation_id, conversationData?.STATUS, refetchConversationList]);

  const handleNavigateToQuery = (queryDetailsData: any) => {
    if (queryDetailsData?.IS_EDITABLE === "Y") {
      navigate(
        RouteConstants.ROUTE_QUERY_DETAILS.replace(
          ":query_id",
          conversationData?.QUERY_ID
        ),
        {
          state: {
            queryData: {
              id: queryDetailsData?.QUERY_ID,
              patientId: queryDetailsData?.PATIENT_ID,
            },
            from: previousRoute,
          },
        }
      );
    } else if (queryDetailsData?.IS_EDITABLE === "N") {
      getHtmlData(queryDetailsData?.QUERY_ID, queryDetailsData?.QUERY_STATUS_ID);
    }
  };

  const handleSend = async () => {
    if (message.trim()) {
      const newMessage: Message = {
        MESSAGE_ID: Date.now().toString(),
        CONVERSATION_ID: conversation_id || "",
        SENDER_ID: "PROVIDER",
        SENDER_TYPE: "PROVIDER",
        MESSAGE_TEXT: message,
        SENT_DATE: new Date().toISOString(),
        IS_READ: "N",
        IS_RECEIVED: null,
        LAST_UPDATED: null,
        SOURCE: null,
        UPDATED_BY: null,
      };
      setMessageList((prev) => [...prev, newMessage]);
      setMessage("");
      await handlePostNewMessage(newMessage, providerNPI);
    }
  };

  // Handle chat status button click - shows modal first
  const handleChatStatusClick = () => {
    const isCurrentlyOpen = conversationData?.STATUS === "OPEN";
    setModalType(isCurrentlyOpen ? "end" : "reopen");
    setIsModalOpen(true);
  };

  // Handle actual chat status update after modal confirmation
  const handleChatUpdate = async () => {
    setChatStatusUpdating(true);
    const requestBody = {
      conversation_id: conversationData?.CONVERSATION_ID,
      provider_npi: providerNPI,
      status: conversationData?.STATUS === "OPEN" ? "CLOSED" : "OPEN",
    };

    try {
      const response = await updateConversationStatusMutation.mutateAsync(
        requestBody
      );
      setConversationData({
        ...conversationData,
        STATUS: response?.data?.data?.status,
      });
      setMessage("");
      setIsModalOpen(false); // Close modal after successful update
    } catch (error) {
      console.error("Error updating status of conversation:", error);
    } finally {
      setChatStatusUpdating(false);
    }
  };

  // Handle modal close for reset modal
  const handleResetModalClose = () => {
    setIsModalOpen(false);
    setModalType(null);
  };

  // Handle modal confirm
  const handleModalConfirm = () => {
    handleChatUpdate();
  };

  // Refactored handleScrollNewMessage to use containerRef
  const handleScrollNewMessage = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  // Scroll to bottom whenever messageList changes
  useEffect(() => {
    handleScrollNewMessage();
  }, [messageList.length]);

  const admission = formatDate(
    queryDetails?.ADMISSION_DATE,
    DATE_FORMAT.MM_DD_YYYY
  );
  const discharge = formatDate(
    queryDetails?.DISCHARGE_DATE,
    DATE_FORMAT.MM_DD_YYYY
  );
  const los = queryDetails?.LOS != null ? `${queryDetails.LOS} days` : "-";

  const extractInitialsFromName = useCallback((fullName: string): string => {
    const parts = fullName.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return `${first}${last}`.toUpperCase() || "PR";
  }, []);

  const getCurrentPractitionerInitials = useCallback(
    async (client: any): Promise<string> => {
      if (!client?.user) {
        console.warn("FHIR client or user not available");
        return "PR";
      }

      try {
        if (typeof client.user.name === "string") {
          return extractInitialsFromName(client.user.name);
        }

        const practitionerRef =
          client.user.fhirUser ||
          (client.user.id && `Practitioner/${client.user.id}`);

        if (!practitionerRef) {
          console.warn("No practitioner reference available");
          return "PR";
        }

        const practitionerId = practitionerRef.split("/").pop();
        const practitioner = (await client.request(
          `Practitioner/${practitionerId}`
        )) as FhirPractitioner;

        const fhirName = practitioner?.name?.[0];
        if (!fhirName) return "PR";

        const firstName = fhirName.given?.[0] ?? "";
        const lastName = fhirName.family ?? "";

        const initials = `${firstName[0] ?? ""}${
          lastName[0] ?? ""
        }`.toUpperCase();
        return initials || "PR";
      } catch (error) {
        console.error("Error getting practitioner initials:", error);
        return "PR";
      }
    },
    [extractInitialsFromName]
  );

  useEffect(() => {
    const initializeFhirClient = async () => {
      try {
        const client = await FHIR.oauth2.ready();
        const name = await getCurrentPractitionerInitials(client);
        setCurrentPractitionerName(name);
      } catch (error) {
        console.warn("FHIR client not available:", error);
        setCurrentPractitionerName("PR");
      }
    };

    initializeFhirClient();
  }, [getCurrentPractitionerInitials]);

  console.log("PREVIOUS ROUTE ON MESSAGES");
  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        overflowY: "hidden",
        height: "96vh",
      }}
    >
      <Box pb={"sm"}>
        <InfoPanel
          data={{
            "Admit Date / Discharge Date / LOS": `${admission} / ${discharge} $ ${los}`,
            "Patient Name / MRN": `${queryDetails?.PATIENT_NAME} $ ${queryDetails?.MRN_NO}`,
            "Gender / Age": `${queryDetails?.PATIENT_SEX} / ${queryDetails?.PATIENT_AGE}y`,
            "Visit ID": queryDetails?.VISIT_ID,
            "Facility Name": queryDetails?.FACILITY_NAME,
          }}
          multiLineKeys={[
            "Admit Date / Discharge Date / LOS",
            "Patient Name / MRN",
          ]}
        />
      </Box>

      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          flexWrap: "nowrap",
          width: "100%",
          overflowX: "auto",
        }}
      >
        {/* Left Side - Avatar + Query ID */}
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            flex: "1 1 auto",
            gap: "8px",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Avatar
            variant='filled'
            size={isSmallScreen ? "26px" : "32px"}
            radius='xl'
            color={colors.primary[5]}
          >
            <IconHome
              size={isSmallScreen ? 14 : 18}
              style={{ cursor: "pointer" }}
              onClick={() => navigate(getMessagesListRoute(location.pathname))}
            />
          </Avatar>

          <Group
            gap={isSmallScreen ? "2px" : "4px"}
            style={{ flexWrap: "wrap" }}
          >
            <Text
              fw={400}
              size={isSmallScreen ? "12px" : "14px"}
              lh='22px'
              c={colors.gray[7]}
            >
              Query ID:
            </Text>
            <Text
              fw={500}
              size={isSmallScreen ? "12px" : "14px"}
              lh='20px'
              c={colors.gray[8]}
              style={{ whiteSpace: "nowrap" }}
            >
              {conversationData?.QUERY_ID}
            </Text>
            <div>
              <Badge
                color={
                  conversationData?.STATUS === "OPEN"
                    ? colors.warning[4]
                    : colors.success[6]
                }
                variant='light'
                radius='sm'
                size={"xs"}
                style={{
                  border: `1px solid ${
                    conversationData?.STATUS === "OPEN"
                      ? colors.warning[4]
                      : colors.success[6]
                  }`,
                  height: "22px",
                  lineHeight: "24px",
                  padding: "0px 4px",
                  marginLeft: "8px",
                  marginBottom: "4px",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                  justifyContent: "center",
                  alignItems: "center",
                  minWidth: "75px",
                  maxWidth: "120px",
                }}
              >
                {conversationData?.STATUS}
              </Badge>
            </div>
          </Group>
        </Box>

        {/* Right Side - Button Group */}
        <Box
          style={{
            display: "flex",
            gap: "8px",

            flexWrap: "nowrap",
            justifyContent: "flex-end",
          }}
        >
          {!(queryDetails?.IS_EDITABLE === "N") && (
            <Button
              style={{
                transition: "none",
                transform: "none",
                "&:active": {
                  transform: "none",
                },
                "&:active:not(:disabled)": {
                  transform: "none",
                },
              }}
              size={isSmallScreen ? "xs" : "sm"}
              variant='outline'
              color={
                conversationData?.STATUS === "OPEN"
                  ? colors.danger[4]
                  : colors.success[6]
              }
              onClick={handleChatStatusClick}
            >
              {conversationData?.STATUS === "OPEN"
                ? "End Chat"
                : "Re-open Chat"}
            </Button>
          )}

          <Button
            size={isSmallScreen ? "xs" : "sm"}
            variant='filled'
            onClick={() => {
              handleNavigateToQuery(queryDetails);
            }}
            loading={htmlViewerLoading}
            disabled={htmlViewerLoading}
            style={{
              backgroundColor: colors.primary[5],
              transition: "none",
              transform: "none",
              "&:active": {
                transform: "none",
              },
              "&:active:not(:disabled)": {
                transform: "none",
              },
            }}
          >
            {queryDetails?.IS_EDITABLE === "N" ? "View Query" : "Go to query"}
          </Button>
        </Box>
      </Box>

      <Box
        pt={"12px"}
        pb={"12px"}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {prevMessageLoading && <Loader color={colors.primary[5]} size='xs' />}
      </Box>

      <Box
        className='message-scroll-container'
        ref={containerRef}
        style={{
          flex: 1,
          overflowY: "auto",
          backgroundColor: "#fff",
          borderRadius: "8px",
          paddingRight: "6px",
        }}
      >
        {/* Sentinel div to trigger fetching on scroll to top */}
        <div ref={topSentinelRef} style={{ height: 1 }} />
        {messageList?.map((message, index) => {
          const isBeingSent = sendingMessageId === message.MESSAGE_ID;
          const hasFailed = failedMessageIds.has(message.MESSAGE_ID);

          return (
            <Box
              pb={"24px"}
              key={index}
              style={{
                display: "flex",
                justifyContent:
                  message?.SENDER_TYPE === "PROVIDER"
                    ? "flex-end"
                    : "flex-start",
              }}
            >
              {message?.SENDER_TYPE === "USER" && (
                <Avatar
                  style={{
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    opacity: isBeingSent ? 0.5 : 1,
                  }}
                  mr={"10px"}
                  src={message?.SENDER_TYPE}
                  color='#FFFFFF'
                  radius='xl'
                >
                  <Image src={botSvg} />
                </Avatar>
              )}
              <Box
                style={{
                  maxWidth: "50%",
                  backgroundColor: hasFailed
                    ? "#ffebee" // Light red background for failed messages
                    : message?.SENDER_TYPE === "USER"
                    ? colors.gray[2]
                    : colors.primary[10],
                  color: hasFailed
                    ? "#d32f2f" // Red text for failed messages
                    : message?.SENDER_TYPE === "USER"
                    ? colors.gray[7]
                    : colors.gray[8],
                  padding: "10px 15px",
                  borderRadius: "8px",
                  opacity: isBeingSent ? 0.5 : 1,
                  border: hasFailed ? "1px solid #f44336" : "none", // Red border for failed messages
                }}
              >
                <Text
                  fw={500}
                  lh={"20px"}
                  size='16px'
                  style={{
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {message?.MESSAGE_TEXT}
                </Text>
                <Text
                  mt={12}
                  fw={400}
                  lh={"22px"}
                  style={{
                    display: "flex",
                    justifyContent:
                      message?.SENDER_TYPE === "USER"
                        ? "flex-start"
                        : "flex-end",
                  }}
                  size='14px'
                  c={
                    hasFailed
                      ? "#d32f2f" // Red color for failed message status
                      : message?.SENDER_TYPE === "USER"
                      ? colors.gray[6]
                      : colors.gray[6]
                  }
                >
                  {isBeingSent
                    ? "Sending..."
                    : hasFailed
                    ? "Failed to send"
                    : formatDateWithZoneAbbreviation(
                        message?.SENT_DATE,
                        provider?.timezone_info?.timezone_name,
                        provider?.timezone_info?.abbrev_simple
                      )}
                </Text>
              </Box>
              {message?.SENDER_TYPE === "PROVIDER" && (
                <Avatar
                  variant='filled'
                  color={hasFailed ? "#f44336" : colors.primary[10]}
                  ml={"10px"}
                  src={message?.SENDER_TYPE}
                  radius='xl'
                  style={{
                    opacity: isBeingSent ? 0.5 : 1,
                  }}
                >
                  <Text fw={700} size='16px' lh={"24px"} c={colors.gray[8]}>
                    {currentPractitionerName ? currentPractitionerName : "PR"}
                  </Text>
                </Avatar>
              )}
            </Box>
          );
        })}
      </Box>

      <Box w='100%'>
        {/* Show receiving loader when new messages are being fetched */}
        {newMessageLoading && (
          <Box
            pb={"12px"}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Loader color={colors.primary[5]} size='xs' />
          </Box>
        )}

        <Textarea
          maxLength={1000}
          placeholder='Write your message here...'
          value={message}
          disabled={conversationData?.STATUS === "CLOSED"}
          onChange={(event) => setMessage(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              handleSend();
            }
          }}
          w='100%'
          variant='unstyled'
          size='lg'
          rightSectionWidth={60}
          rightSection={
            <Box pr={6} mb={2}>
              <ActionIcon
                variant='outline'
                radius='xl'
                size='xl'
                p={"8px"}
                color={colors.gray[4]}
                onClick={handleSend}
                disabled={
                  conversationData?.STATUS === "CLOSED" || !message.trim()
                }
              >
                <IconSend2 height={40} width={40} color={colors.gray[8]} />
              </ActionIcon>
            </Box>
          }
          styles={{
            input: {
              padding: "12px 60px 12px 16px",
              height: "64px",
              borderRadius: "8px",
              border: `1px solid ${colors.gray[4]}`,
              fontSize: "16px",
              lineHeight: "22px",
            },
          }}
        />
      </Box>

      {/* HTML Viewer Modal */}
      <Modal
        opened={opened}
        onClose={handleModalClose}
        size='xl'
        centered
        styles={{
          body: { padding: 0 },
          header: { borderBottom: "1px solid #e9ecef" },
        }}
        withCloseButton={false}
      >
        <div style={{ height: "80vh", position: "relative" }}>
          {htmlViewerLoading && (
            <LoadingOverlay
              visible={htmlViewerLoading}
              zIndex={1000}
              overlayProps={{ radius: "sm", blur: "2" }}
              loaderProps={{ color: colors.primary[5], size: "md" }}
            />
          )}
          <HtmlViewer
            htmlContent={htmlData}
            queryId={rowID}
            onClose={handleModalClose}
            status={rowStatus}
          />
        </div>
      </Modal>

      {/* Reset Modal for Chat Status Confirmation */}
      <ResetModal
        isOpen={isModalOpen}
        onClose={handleResetModalClose}
        onConfirm={handleModalConfirm}
        primaryColor={colors.primary[5]}
        open={modalType === "reopen"}
        end={modalType === "end"}
      />

      <LoadingOverlay
        visible={
          (queryDetailsLoading && !queryDetails) ||
          messagesInitialLoading ||
          chatStatusUpdating
        }
        zIndex={999}
        overlayProps={{ radius: "sm", blur: "2" }}
      />
    </Box>
  );
};

export default MessageBox;
